import React, { Suspense } from 'react'

import { TranslationsDegradedNotice } from '@platform/i18n/components/TranslationsDegradedNotice'
import { applyCriticalTranslationFallback, initializeI18n } from '@platform/i18n/config'
import { bootI18nWithinBudget } from '@platform/i18n/i18n-boot'
import ReactDOM from 'react-dom/client'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { API_BASE_URL } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

import './index.css'

import App from './App'
import { I18nWrapper } from './components/I18nWrapper'

/**
 * Non-translated boot fallback. Deliberately plain: it is the LAST resort while
 * React resolves a lazy chunk, so it must never depend on i18n — a fallback
 * that suspends again would leave the root empty (the blank-page bug).
 */
const BootFallback: React.FC = () => (
  <div className="min-h-screen bg-crypto-dark flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-crypto-primary border-t-transparent rounded-full animate-spin" />
  </div>
)

/**
 * Mount the React tree. `translationsUsable` tells the i18n gate whether text
 * can be rendered now — either the backend catalog loaded, or the bundled
 * critical fallback was seeded.
 */
const renderApp = (translationsUsable: boolean): void => {
  const rootElement = document.getElementById('root')
  if (!rootElement) throw new Error('Root element not found')
  const root = ReactDOM.createRoot(rootElement)

  root.render(
    <React.StrictMode>
      {/* Outermost boundary: a render error anywhere (providers included) must
          show a message, never unmount the tree into a blank white page. */}
      <ErrorBoundary>
        <Suspense fallback={<BootFallback />}>
          <I18nWrapper i18nReady={translationsUsable}>
            <TranslationsDegradedNotice />
            <App />
          </I18nWrapper>
        </Suspense>
      </ErrorBoundary>
    </React.StrictMode>,
  )
}

/**
 * Start the React application.
 *
 * i18n initialization runs under a BUDGET: translations are fetched from the
 * backend, and a slow/dead backend must DEGRADE the UI, never stop it from
 * rendering (the app used to await the full load before the first render, so an
 * aborted namespace fetch left `#root` empty — including on /login).
 */
async function startApp() {
  logger.info('[App] Initializing i18n...')

  const bootOutcome = await bootI18nWithinBudget({
    initializeI18n,
    applyCriticalFallback: applyCriticalTranslationFallback,
  })

  if (bootOutcome.initializedWithinBudget) {
    logger.info('[App] i18n initialization complete')
  } else {
    logger.warn(
      `[App] i18n degraded — bundled fallback serving: ${
        bootOutcome.seededFallbackNamespaces.join(', ') || 'nothing'
      }`,
    )
  }

  renderApp(bootOutcome.translationsUsable)

  if (import.meta.env.DEV) {
    logger.info('[App] React application started')
    // Log what the app ACTUALLY calls, not a fabricated fallback: an empty
    // API_BASE_URL means same-origin (the dev server's /api proxy handles it).
    logger.info(
      '[App] Backend URL:',
      API_BASE_URL === '' ? `${window.location.origin} (same-origin, via dev proxy)` : API_BASE_URL,
    )
  }
}

// Start the application
startApp().catch((error) => {
  logger.error('[App] Failed to start application:', error)

  // Last resort: the app must appear even if the boot sequence itself threw.
  renderApp(true)
})
